// Schema definitions for each registry — drives the generic AssetRegistry form.
import { Gauge, Container, GitBranchPlus, ArrowDownToLine, Wind, Bell, Droplets, Zap } from "lucide-react";

const COMMON = [
  { key: "name", label: "Name", required: true, full: true },
  { key: "location_label", label: "Location Label", placeholder: "e.g. Pump Room / Tower A — Stair-A", full: true },
  { key: "location", label: "Location Code", placeholder: "basement / terrace / tower_a", kind: "select",
    options: [{ v: "basement", label: "Basement" }, { v: "ground", label: "Ground" }, { v: "terrace", label: "Terrace" }, { v: "tower_a", label: "Tower A" }, { v: "tower_b", label: "Tower B" }, { v: "tower_c", label: "Tower C" }, { v: "pump_room", label: "Pump Room" }, { v: "rooftop", label: "Rooftop" }, { v: "other", label: "Other" }] },
  { key: "make", label: "Make" },
  { key: "model", label: "Model" },
  { key: "status", label: "Status", kind: "select",
    options: ["active", "fault", "offline", "maintenance"].map((v) => ({ v, label: v.toUpperCase() })) },
  { key: "last_tested", label: "Last Tested", kind: "date" },
  { key: "next_service", label: "Next Service / Calibration", kind: "date" },
  { key: "notes", label: "Notes", kind: "textarea", full: true },
];

export const REGISTRY_DEFS = {
  pressure_sensor: {
    title: "Pressure Sensors", icon: Gauge, accentColor: "orange",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster", "terrace_header"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.zone", label: "Zone", kind: "select",
        options: [{ v: "high_zone", label: "High Zone" }, { v: "low_zone", label: "Low Zone" }, { v: "single_zone", label: "Single Zone" }] },
      { key: "meta.tower", label: "Tower (if booster)" },
      { key: "meta.range_bar", label: "Range (bar)", kind: "number" },
      { key: "meta.alarm_high_bar", label: "Alarm High (bar)", kind: "number", required: true, hint: "Mandatory — set per site" },
      { key: "meta.alarm_low_bar", label: "Alarm Low (bar)", kind: "number", required: true, hint: "Mandatory — set per site" },
      ...COMMON.slice(3),
    ],
  },
  tank: {
    title: "Tanks", icon: Container, accentColor: "blue",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.type", label: "Tank Type", kind: "select",
        options: ["UG", "OHT", "break_tank", "terrace", "sprinkler_reserve", "day_tank", "diesel"].map((v) => ({ v, label: v.toUpperCase() })) },

      // ── DIMENSIONS ────────────────────────────────────────────
      { key: "_section_dim", label: "Tank Dimensions (auto-calculates capacity)", kind: "section" },
      { key: "meta.shape", label: "Shape", kind: "select",
        options: [{ v: "rectangular", label: "Rectangular / Cuboidal" }, { v: "cylindrical_vertical", label: "Cylindrical (vertical)" }, { v: "cylindrical_horizontal", label: "Cylindrical (horizontal)" }],
        default: "rectangular" },
      { key: "meta.length_m", label: "Length (m)", kind: "number", step: "0.01", placeholder: "e.g. 6.0" },
      { key: "meta.width_m", label: "Width (m)", kind: "number", step: "0.01", placeholder: "e.g. 4.0" },
      { key: "meta.height_m", label: "Height / Depth (m)", kind: "number", step: "0.01", placeholder: "e.g. 1.8" },
      { key: "meta.diameter_m", label: "Diameter (m, cylindrical)", kind: "number", step: "0.01", placeholder: "e.g. 3.0" },
      { key: "meta.computed_capacity_litres", label: "Computed Capacity", kind: "computed",
        compute: (form) => {
          const m = form.meta || {};
          const L = Number(m.length_m), W = Number(m.width_m), H = Number(m.height_m), D = Number(m.diameter_m);
          let v = 0;
          if (m.shape === "rectangular") v = L * W * H;
          else if (m.shape === "cylindrical_vertical") v = Math.PI * (D / 2) ** 2 * H;
          else if (m.shape === "cylindrical_horizontal") v = Math.PI * (D / 2) ** 2 * L;
          if (!isFinite(v) || v <= 0) return "—";
          const litres = Math.round(v * 1000);
          return `${litres.toLocaleString("en-IN")} L  (${(litres / 1000).toFixed(2)} KL)`;
        } },
      { key: "meta.capacity_kl", label: "Capacity Override (KL)", kind: "number", step: "0.1", placeholder: "leave blank to use computed" },
      { key: "meta.material", label: "Tank Material", placeholder: "RCC / SS304 / HDPE" },

      // ── LEVEL TRANSMITTER (FST700-101 defaults) ───────────────
      { key: "_section_tx", label: "Level Transmitter (FST700-101 default)", hint: "FirstRate FST700-101 Submersible 0–2M 4-20mA 9-30V", kind: "section" },
      { key: "meta.tx_model", label: "Transmitter Model", default: "FST700-101", placeholder: "FST700-101" },
      { key: "meta.tx_range_m", label: "Range (m H₂O)", kind: "number", step: "0.1", default: 2 },
      { key: "meta.tx_signal_output", label: "Signal Output", kind: "select",
        options: [{ v: "4-20mA", label: "4-20mA" }, { v: "0-10V", label: "0-10V" }, { v: "Modbus RTU", label: "Modbus RTU" }, { v: "HART", label: "HART" }],
        default: "4-20mA" },
      { key: "meta.tx_supply_voltage", label: "Supply Voltage", default: "9-30V DC", placeholder: "9-30V DC" },
      { key: "meta.tx_accuracy", label: "Accuracy", default: "±0.5% FS", placeholder: "±0.5% FS" },
      { key: "meta.tx_cable_length_m", label: "Cable Length (m)", kind: "number", step: "0.1", default: 3 },
      { key: "meta.tx_probe_diameter_mm", label: "Probe Diameter (mm)", kind: "number", default: 28 },
      { key: "meta.tx_material", label: "Probe Material", default: "316L", placeholder: "316L" },
      { key: "meta.tx_calibration_date", label: "Last Calibration", kind: "date" },
      { key: "meta.tx_serial", label: "Transmitter Serial #" },

      // ── VALVES / SETPOINTS ────────────────────────────────────
      { key: "_section_op", label: "Valves & Setpoints", kind: "section" },
      { key: "meta.inlet_valve_type", label: "Inlet Valve Type" },
      { key: "meta.outlet_valve_type", label: "Outlet Valve Type" },
      { key: "meta.level_high_pct", label: "Level High (%)", kind: "number" },
      { key: "meta.level_low_pct", label: "Level Low (%)", kind: "number" },
      { key: "meta.level_low_low_pct", label: "Level Low-Low (%)", kind: "number" },

      // ── COMMON FOOTER ─────────────────────────────────────────
      { key: "_section_meta", label: "Asset Metadata", kind: "section" },
      ...COMMON.slice(3),
    ],
  },
  valve: {
    title: "Valves", icon: GitBranchPlus, accentColor: "emerald",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster", "ventilation", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.type", label: "Valve Type", kind: "select",
        options: ["gate", "butterfly", "NRV", "globe", "ball"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.size_mm", label: "Size (mm)", kind: "number" },
      { key: "meta.normally", label: "Normally", kind: "select", options: ["open", "closed"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.actuator_type", label: "Actuator", kind: "select", options: ["manual", "motorized", "pneumatic"].map((v) => ({ v, label: v.toUpperCase() })) },
      ...COMMON.slice(3),
    ],
  },
  lift: {
    title: "Lifts", icon: ArrowDownToLine, accentColor: "pink",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.lift_type", label: "Lift Type", kind: "select",
        options: ["passenger", "service", "firefighter"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.capacity_kg", label: "Capacity (kg)", kind: "number" },
      { key: "meta.stops", label: "Stops", kind: "number" },
      { key: "meta.recall_floor", label: "Recall Floor", kind: "number" },
      { key: "meta.firefighter_mode", label: "Firefighter Mode", kind: "select",
        options: [{ v: "Phase1", label: "Phase 1 only" }, { v: "Phase2", label: "Phase 2 only" }, { v: "Both", label: "Phase 1 & 2" }] },
      { key: "meta.phase1_tested", label: "Phase 1 Last Tested", kind: "date" },
      { key: "meta.phase2_tested", label: "Phase 2 Last Tested", kind: "date" },
      { key: "_section_telemetry", label: "Live Telemetry Links (optional)", hint: "Same gateway as the Floor link above — leave blank if not available", kind: "section" },
      { key: "meta.direction_io_point_name", label: "Direction Register", placeholder: "e.g. lift1-direction" },
      { key: "meta.door_io_point_name", label: "Door Status Register", placeholder: "e.g. lift1-door" },
      { key: "meta.motor_temp_io_point_name", label: "Motor Temp Register", placeholder: "e.g. lift1-motor-temp" },
      ...COMMON.slice(3),
    ],
  },
  fan: {
    title: "Fans & AHU", icon: Wind, accentColor: "cyan",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["ventilation", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.type", label: "Fan Type", kind: "select",
        options: ["pressurization", "exhaust", "fresh_air", "AHU"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.cfm", label: "CFM Capacity", kind: "number" },
      { key: "meta.kw", label: "kW Rating", kind: "number" },
      { key: "meta.control", label: "Control Type", kind: "select", options: ["on_off", "VFD"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.associated_zone", label: "Associated Zone" },
      ...COMMON.slice(3),
    ],
  },
  fire_alarm_device: {
    title: "Fire Alarm Devices", icon: Bell, accentColor: "red",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["detection", "other"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.device_type", label: "Device Type", kind: "select",
        options: ["smoke", "heat", "MCP", "sounder", "beacon", "io_module"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.loop", label: "Loop #", kind: "number" },
      { key: "meta.address", label: "Address", kind: "number" },
      { key: "meta.zone", label: "Zone" },
      { key: "meta.floor", label: "Floor" },
      ...COMMON.slice(3),
    ],
  },
  pump: {
    title: "Pumps", icon: Droplets, accentColor: "blue",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "system", label: "System", kind: "select",
        options: ["hydrant", "sprinkler", "booster"].map((v) => ({ v, label: v.toUpperCase() })) },
      { key: "meta.pump_type", label: "Pump Type", kind: "select",
        options: ["main_pump", "jockey_pump", "booster_pump", "sprinkler_pump", "hydrant_pump", "pressure_pump", "transfer_pump", "dewatering_pump", "drain_pump", "sewage_pump", "borewell_pump", "domestic_water_pump", "raw_water_pump", "soft_water_pump", "fire_pump"].map((v) => ({ v, label: v.replace(/_/g, " ").toUpperCase() })) },
      { key: "meta.capacity_lpm", label: "Capacity (LPM)", kind: "number" },
      { key: "meta.kw", label: "Power (kW)", kind: "number" },
      { key: "meta.power_source", label: "Power Source", kind: "select",
        options: ["Electric", "Diesel", "Both"].map((v) => ({ v, label: v })) },
      { key: "meta.serial_number", label: "Serial Number" },
      { key: "meta.commissioned_date", label: "Commissioned Date", kind: "date" },
      { key: "_section_detect", label: "Detection Links (optional)", hint: "Pump Run Detection uses the Live Data Source link below — these are separate signals, only if this pump's controller reports them.", kind: "section" },
      { key: "meta.pump_trip_io_point_name", label: "Pump Trip Register", placeholder: "e.g. pump1-trip" },
      { key: "meta.auto_manual_io_point_name", label: "Auto/Manual Register", placeholder: "e.g. pump1-auto-manual" },
      ...COMMON.slice(3),
    ],
  },
  mcc_panel: {
    title: "MCC Panels", icon: Zap, accentColor: "amber",
    schema: [
      ...COMMON.slice(0, 3),
      { key: "meta.incoming_voltage", label: "Incoming Voltage (V)", kind: "number" },
      { key: "meta.rated_capacity_amp", label: "Rated Capacity (A)", kind: "number" },
      { key: "meta.number_of_feeders", label: "Number of Feeders", kind: "number" },
      { key: "_section_detect", label: "Detection Links (optional)", hint: "Panel Healthy uses the Live Data Source link below — power status is conditional, only if this panel reports it separately.", kind: "section" },
      { key: "meta.power_status_io_point_name", label: "Power Status Register", placeholder: "e.g. mcc1-power-ok" },
      ...COMMON.slice(3),
    ],
  },
};
